import React from 'react';
import { motion } from 'framer-motion';
import { Layers, Database, Brain, RefreshCw } from 'lucide-react';
const layers = [{
  icon: Brain,
  label: 'AI Intelligence Engine',
  sublabel: '',
  description: 'Proprietary AI models that analyze data and generate personalized content',
  color: 'violet',
  modules: ['Blueprint Analysis', 'Story Generation', 'Content Creation', 'Content and Channel Optimization']
}, {
  icon: Database,
  label: 'Data Supply Chain',
  sublabel: '',
  description: 'Secure data pipeline for regulatory-compliant content and insights',
  color: 'indigo',
  modules: ['Therapeutic Area Themes', 'Product and Brand Data', 'HCP & Patient Insights', 'Market Intelligence']
}, {
  icon: Layers,
  label: 'Application Suite',
  sublabel: 'Foundation and Moat',
  description: 'Core content creation and distribution platform for creating, managing, and optimizing marketing campaigns',
  color: 'purple',
  modules: ['Snak Composer', 'Snakker Player — Choice Driven', 'Distribution', 'Analytics']
}];
const colorMap: Record<string, {
  bg: string;
  border: string;
  text: string;
  badge: string;
  badgeText: string;
  module: string;
  moduleText: string;
}> = {
  purple: {
    bg: 'bg-purple-50',
    border: 'border-purple-200',
    text: 'text-purple-600',
    badge: 'bg-purple-100',
    badgeText: 'text-purple-700',
    module: 'bg-white border border-purple-100',
    moduleText: 'text-gray-700'
  },
  indigo: {
    bg: 'bg-indigo-50',
    border: 'border-indigo-200',
    text: 'text-indigo-600',
    badge: 'bg-indigo-100',
    badgeText: 'text-indigo-700',
    module: 'bg-white border border-indigo-100',
    moduleText: 'text-gray-700'
  },
  violet: {
    bg: 'bg-violet-50',
    border: 'border-violet-200',
    text: 'text-violet-600',
    badge: 'bg-violet-100',
    badgeText: 'text-violet-700',
    module: 'bg-white border border-violet-100',
    moduleText: 'text-gray-700'
  }
};
export function PlatformSlide() {
  return <div className="h-full flex flex-col gap-4" data-id="element-258">
      {/* Tag + subtitle */}
      <motion.div initial={{
      opacity: 0,
      y: -8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1
    }} className="flex items-center gap-3" data-id="element-259">
        <span className="text-[10px] font-bold uppercase tracking-widest text-purple-600 bg-purple-100 px-2.5 py-1 rounded-full" data-id="element-260">
          Platform Architecture
        </span>
        <span className="text-sm text-gray-400 italic flex items-center gap-1.5" data-id="element-261">
          <RefreshCw className="w-3.5 h-3.5 text-purple-400" data-id="element-262" />
          Think · Create · Learn · Improve
        </span>
      </motion.div>

      {/* Three layers */}
      <div className="flex-1 flex flex-col gap-3 min-h-0" data-id="element-263">
        {layers.map((layer, idx) => {
        const c = colorMap[layer.color];
        const Icon = layer.icon;
        return <motion.div key={idx} initial={{
          opacity: 0,
          x: -16
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.2 + idx * 0.12
        }} className={`flex-1 rounded-2xl border ${c.border} ${c.bg} px-5 py-4 flex flex-col gap-3`} data-id="element-264">
              {/* Layer header */}
              <div className="flex items-start gap-3" data-id="element-265">
                <div className={`w-9 h-9 rounded-lg ${c.badge} flex items-center justify-center shrink-0`} data-id="element-266">
                  <Icon className={`w-5 h-5 ${c.text}`} data-id="element-267" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-268">
                  <div className="flex items-baseline gap-2 flex-wrap" data-id="element-269">
                    <span className={`text-base font-bold ${c.text}`} data-id="element-270">
                      {layer.label}
                    </span>
                    {layer.sublabel && <span className="text-base text-gray-500 font-medium" data-id="element-271">
                        {layer.sublabel}
                      </span>}
                  </div>
                  <p className="text-sm text-gray-500 mt-0.5 leading-snug" data-id="element-272">
                    {layer.description}
                  </p>
                </div>
              </div>

              {/* Module chips */}
              <div className="grid grid-cols-4 gap-2" data-id="element-273">
                {layer.modules.map((mod, mIdx) => <motion.div key={mIdx} initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: 0.35 + idx * 0.12 + mIdx * 0.05
            }} className={`${c.module} rounded-lg px-3 py-2.5 text-sm font-semibold ${c.moduleText} text-center leading-snug shadow-sm`} data-id="element-274">
                    {mod}
                  </motion.div>)}
              </div>
            </motion.div>;
      })}
      </div>

      {/* Footer */}
      <motion.p initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 0.8
    }} className="text-center text-xs text-gray-400 pb-1" data-id="element-275">
        Proprietary AI trained on{' '}
        <span className="font-bold text-gray-600" data-id="element-276">
          content, audience, channels and regional performance data
        </span>
      </motion.p>
    </div>;
}